"use client";

import { useCallback, useEffect, useRef, useState } from "react";

const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

// After a dialog opened from the floating dock closes, move focus to the page
// content nearest the viewport rather than back into the dock — so keyboard
// users continue tabbing through the page instead of being dropped back onto
// the dock.
function focusNearestSection() {
  const main = document.getElementById("main-content");
  if (!main) return;
  const vh = window.innerHeight;
  let target: HTMLElement | null = null;
  for (const el of main.querySelectorAll<HTMLElement>(FOCUSABLE)) {
    const r = el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) continue;
    // First focusable that's within the current viewport.
    if (r.bottom > 8 && r.top < vh) {
      target = el;
      break;
    }
  }
  (target ?? main).focus({ preventScroll: true });
}

// Drop-in replacement for `useState<boolean>` for dialogs/drawers/menus opened
// programmatically (not via a `<Dialog.Trigger>`). Safari/WebKit doesn't
// restore focus to the opener in that case, so we capture the element that had
// focus when the dialog opened and restore focus on close — deferred a frame so
// the dialog's focus trap has released.
//
// - Opened from a normal page element  → focus returns to that element.
// - Opened from the floating dock, or the opener was removed (one drawer
//   opening another, e.g. the menu's "Book a consultation") → focus moves to
//   the nearest page section so tabbing continues through the page.
export function useOpenWithFocus(initial = false) {
  const [open, setOpen] = useState(initial);
  const triggerRef = useRef<HTMLElement | null>(null);
  const wasOpen = useRef(initial);

  const setOpenWithCapture = useCallback((next: boolean) => {
    if (next) {
      const active = document.activeElement as HTMLElement | null;
      if (active && active !== document.body && active.isConnected) {
        triggerRef.current = active;
        active.blur();
      }
    }
    setOpen(next);
  }, []);

  useEffect(() => {
    const justClosed = wasOpen.current && !open;
    wasOpen.current = open;
    if (!justClosed) return;

    const el = triggerRef.current;
    triggerRef.current = null;

    let raf = 0;
    let attempts = 0;
    // A closing drawer stays mounted through its exit animation, and keeps the
    // background aria-hidden/inert until then. Restoring focus before that lifts
    // would land on a hidden element ("aria-hidden on a focused element"), so we
    // wait (capped at ~1s) for the hiding to clear before focusing.
    const isHidden = (node: Element | null) =>
      !!node && !!node.closest('[aria-hidden="true"],[inert]');

    const restore = () => {
      const useTrigger = !!(
        el &&
        el.isConnected &&
        !el.closest("[data-floating-dock]")
      );
      const target = useTrigger ? el : document.getElementById("main-content");
      if (attempts < 60 && isHidden(target)) {
        attempts += 1;
        raf = requestAnimationFrame(restore);
        return;
      }
      if (useTrigger && el) el.focus({ preventScroll: true });
      else focusNearestSection();
    };

    raf = requestAnimationFrame(restore);
    return () => cancelAnimationFrame(raf);
  }, [open]);

  return [open, setOpenWithCapture] as const;
}
